
import React, { ReactNode, useRef, useEffect, useState } from 'react';
import { CheckIcon, XIcon, EyeIcon, EyeSlashIcon } from './IconComponents';

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  className?: string;
  padding?: string;
}

// Vision UI Styled Components

export const Card: React.FC<CardProps> = ({ children, className = '', padding = 'p-5', ...props }) => (
  <div className={`relative flex flex-col min-w-0 break-words bg-white/5 backdrop-blur-xl border border-white/10 shadow-xl rounded-2xl bg-clip-border ${padding} ${className}`} {...props}>
    {children}
  </div>
);

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning';
  children: ReactNode;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ children, className = '', variant = 'primary', ...props }, ref) => {
    const baseClasses = 'inline-flex items-center justify-center px-6 py-2.5 rounded-xl font-bold text-xs uppercase transition-all duration-200 ease-in-out focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed transform hover:-translate-y-0.5 active:translate-y-0 active:scale-95 shadow-lg active:shadow-inner';

    const variantClasses = {
      primary: 'bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:shadow-blue-500/40',
      secondary: 'bg-transparent border border-white/20 text-white hover:bg-white/10',
      success: 'bg-gradient-to-r from-emerald-400 to-teal-500 text-white hover:shadow-emerald-500/40',
      danger: 'bg-gradient-to-r from-red-500 to-orange-500 text-white hover:shadow-red-500/40',
      warning: 'bg-gradient-to-r from-amber-400 to-orange-500 text-white hover:shadow-amber-500/40',
    };

    return (
      <button ref={ref} className={`${baseClasses} ${variantClasses[variant]} ${className}`} {...props}>
        {children}
      </button>
    );
  }
);

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className = '', label, ...props }, ref) => (
    <div className="w-full">
      {label && <label className="block text-xs font-bold text-slate-400 ml-1 mb-2 uppercase">{label}</label>}
      <div className="relative flex items-center w-full rounded-xl border border-white/20 bg-transparent focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/20 transition-all duration-200">
        <input
          ref={ref}
          className={`peer w-full h-full bg-transparent text-sm text-slate-900 dark:text-white font-medium placeholder-slate-500 px-4 py-3 outline-none rounded-xl ${className}`}
          {...props}
        />
      </div>
    </div>
  )
);

interface PasswordInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
}

export const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ className = '', label, ...props }, ref) => {
    const [showPassword, setShowPassword] = useState(false);
    return (
      <div className="w-full">
        {label && <label className="block text-xs font-bold text-slate-400 ml-1 mb-2 uppercase">{label}</label>}
        <div className="relative flex items-center w-full rounded-xl border border-white/20 bg-transparent focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/20 transition-all duration-200">
          <input
            ref={ref}
            type={showPassword ? 'text' : 'password'}
            className={`peer w-full h-full bg-transparent text-sm text-slate-900 dark:text-white font-medium placeholder-slate-500 px-4 py-3 outline-none rounded-xl pr-10 ${className}`}
            {...props}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-white transition-colors"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
          </button>
        </div>
      </div>
    );
  }
);


interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> { }

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className = '', onInput, ...props }, ref) => {
    const internalRef = useRef<HTMLTextAreaElement>(null);
    const combinedRef = (el: HTMLTextAreaElement) => {
      (internalRef as any).current = el;
      if (typeof ref === 'function') {
        ref(el);
      } else if (ref) {
        ref.current = el;
      }
    };

    const autoResize = (el: HTMLTextAreaElement) => {
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    };

    useEffect(() => {
      if (internalRef.current) {
        autoResize(internalRef.current);
      }
    }, [props.value]);


    return (
      <textarea
        ref={combinedRef}
        className={`w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition shadow-sm resize-none overflow-hidden ${className}`}
        onInput={(e) => {
          autoResize(e.currentTarget);
          if (onInput) onInput(e);
        }}
        {...props}
      />
    );
  }
);


interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  children: ReactNode;
}
export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ children, className = '', ...props }, ref) => (
    <div className="relative">
      <select
        ref={ref}
        className={`w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition shadow-sm appearance-none ${className}`}
        {...props}
      >
        {children}
      </select>
      <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
        <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>
  )
);


interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

export const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, children, footer, size = 'lg' }) => {
  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-3xl'
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4" onClick={onClose}>
      <div className={`bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-2xl w-full ${sizeClasses[size]} mx-auto animate-fade-in-up`} onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
          <h3 className="text-xl font-bold text-slate-900 dark:text-white">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors">
            <XIcon className="w-6 h-6" />
          </button>
        </div>
        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {children}
        </div>
        {footer && (
          <div className="p-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700 rounded-b-2xl flex justify-end gap-3">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export const SectionTitle: React.FC<{ children: ReactNode, className?: string }> = ({ children, className }) => (
  <h2 className={`text-xl font-bold text-slate-800 dark:text-white border-b border-slate-200 dark:border-slate-700 pb-2 mb-4 ${className}`}>
    {children}
  </h2>
);

export const Spinner: React.FC = () => (
  <div className="flex justify-center items-center p-4">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-400"></div>
  </div>
);

export const ToggleSwitch: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = ({ className, ...props }) => (
  <label className="relative inline-flex items-center cursor-pointer">
    <input type="checkbox" value="" className="sr-only peer" {...props} />
    <div className="w-11 h-6 bg-slate-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-500/50 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
  </label>
);

export const Toast: React.FC<{ message: string; type: 'success' | 'error'; onClose: () => void }> = ({ message, type, onClose }) => {
  const isSuccess = type === 'success';
  const bgColor = isSuccess ? 'bg-emerald-500' : 'bg-red-500';
  const Icon = isSuccess ? CheckIcon : XIcon;

  return (
    <div className="fixed top-5 right-5 z-50 animate-fade-in-down">
      <div className={`${bgColor} text-white font-semibold rounded-lg shadow-2xl flex items-center gap-4 p-4`}>
        <Icon className="w-6 h-6" />
        <span>{message}</span>
        <button onClick={onClose} className="ml-4 opacity-70 hover:opacity-100">
          <XIcon className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export const Badge: React.FC<{ children: ReactNode, className?: string, variant?: 'success' | 'warning' | 'danger' | 'info' | 'neutral' }> = ({ children, className = '', variant = 'neutral' }) => {
  const variants = {
    success: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
    warning: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
    danger: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    info: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    neutral: 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300'
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${variants[variant]} ${className}`}>
      {children}
    </span>
  );
};